import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  MenuItem,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import {
  GPU_TIERS,
  TRAINING_DEFAULTS,
  SWEEPABLE_PARAMS,
  SWEEP_PRESETS,
  estimateTrainingCost,
} from '../constants/training';
import { createRun } from '../api/runs';
import { fetchRobots, Robot } from '../api/robots';
import apiClient from '../api/client';
import SweepableField from '../components/SweepableField';
import GitHubRepoPicker from '../components/GitHubRepoPicker';
import LinkedError from '../components/LinkedError';

const MAX_TASKS = 500;
const STEPS = ['Source', 'Configure', 'Review'];

interface DiscoveredEnv {
  id: string;
  file: string;
}

const NewRun: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [gpuCount, setGpuCount] = useState<number>(TRAINING_DEFAULTS.gpu_count);
  const [robotId, setRobotId] = useState<string>('');
  const [robots, setRobots] = useState<Robot[]>([]);

  // Repo-driven environment selection
  const [codeUrl, setCodeUrl] = useState('');
  const [codeRef, setCodeRef] = useState('');
  const [discoveredEnvs, setDiscoveredEnvs] = useState<DiscoveredEnv[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<string>('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  // Training settings
  const [numEnvs, setNumEnvs] = useState<number>(TRAINING_DEFAULTS.num_envs);
  const [maxIterations, setMaxIterations] = useState<number>(TRAINING_DEFAULTS.max_iterations);
  const [videoEnabled, setVideoEnabled] = useState<boolean>(TRAINING_DEFAULTS.video_enabled);
  const [videoInterval, setVideoInterval] = useState<number>(TRAINING_DEFAULTS.video_interval);
  const [seed, setSeed] = useState<string>('');

  // Advanced PPO — single values
  const [ppoValues, setPpoValues] = useState<Record<string, string>>({});

  // Sweep state
  const [sweepMode, setSweepMode] = useState(false);
  const [sweepFields, setSweepFields] = useState<Record<string, string[]>>({});
  const [numSeeds, setNumSeeds] = useState<number>(1);
  const [sweepCustomOpen, setSweepCustomOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRobots()
      .then((r) => setRobots(r.filter((robot) => robot.status === 'ready')))
      .catch(() => {});
  }, []);

  // Auto-generate name from robot + env
  useEffect(() => {
    if (nameManuallyEdited) return;
    const robot = robots.find((r) => r.id === robotId);
    const robotName = robot?.name || '';
    const envName = selectedEnv ? selectedEnv.split('/').pop()?.split('-')[0] || selectedEnv : '';
    if (robotName && envName) {
      setName(`${robotName}-${envName}`.toLowerCase().replace(/\s+/g, '-'));
    }
  }, [robotId, selectedEnv, robots, nameManuallyEdited]);

  // Cost calculation
  const totalConfigs = useMemo(() => {
    const sweepLengths = Object.values(sweepFields).filter((v) => v.length >= 2);
    if (sweepLengths.length === 0) return 1;
    return sweepLengths.reduce((acc, vals) => acc * vals.length, 1);
  }, [sweepFields]);

  const totalTasks = Math.max(totalConfigs, 1) * numSeeds;
  const estimate = useMemo(
    () => estimateTrainingCost(numEnvs, maxIterations, gpuCount, totalTasks),
    [numEnvs, maxIterations, gpuCount, totalTasks],
  );

  const activeSweepCount = Object.values(sweepFields).filter(
    (v) => v.length >= 2,
  ).length;

  // Step validation
  const step1Complete = Boolean(codeUrl && selectedEnv && robotId);
  const step2Complete = maxIterations > 0 && gpuCount > 0;

  const handlePpoValueChange = (key: string, value: string) => {
    setPpoValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSweepToggle = (key: string) => {
    setSweepFields((prev) => {
      const next = { ...prev };
      if (key in next) {
        delete next[key];
      } else {
        next[key] = [];
      }
      return next;
    });
  };

  const handleSweepValuesChange = (key: string, values: string[]) => {
    setSweepFields((prev) => ({ ...prev, [key]: values }));
  };

  const applyPreset = (preset: (typeof SWEEP_PRESETS)[number]) => {
    setSweepMode(true);
    setSweepFields({ ...preset.fields });
    setNumSeeds(preset.seeds);
    setSweepCustomOpen(false);
  };

  const clearSweep = () => {
    setSweepMode(false);
    setSweepFields({});
    setNumSeeds(1);
    setSweepCustomOpen(false);
  };

  const handleDiscover = useCallback(async (urlOverride?: string) => {
    const url = urlOverride || codeUrl;
    if (!url.trim()) {
      setDiscoverError('Enter a repo URL');
      return;
    }
    setIsDiscovering(true);
    setDiscoverError(null);
    setDiscoveredEnvs([]);
    setSelectedEnv('');

    try {
      const resp = await apiClient.post('/api/v1/environments/discover', {
        code_url: url.trim(),
        code_ref: codeRef.trim() || undefined,
      });
      const envs: DiscoveredEnv[] = resp.data.environments || [];
      if (envs.length === 0) {
        setDiscoverError('No gym environments found in this repo. Check that it has gym.register() calls.');
      } else {
        setDiscoveredEnvs(envs);
        setSelectedEnv(envs[0].id);
      }
    } catch (e: any) {
      setDiscoverError(
        e.response?.data?.detail || e.message || 'Failed to discover environments',
      );
    } finally {
      setIsDiscovering(false);
    }
  }, [codeUrl, codeRef]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Run name is required');
      return;
    }

    if (!robotId) {
      setError('Select a robot');
      return;
    }

    if (!selectedEnv) {
      setError('Select an environment first (paste a repo URL and click Discover)');
      return;
    }

    if (totalTasks > MAX_TASKS) {
      setError(
        `Too many tasks (${totalTasks}). Maximum is ${MAX_TASKS}. Reduce sweep values or seeds.`,
      );
      return;
    }

    // Validate sweep fields have >= 2 values each
    for (const [key, vals] of Object.entries(sweepFields)) {
      if (vals.length === 1) {
        setError(`Sweep parameter "${key}" needs at least 2 values (or disable sweep for it).`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    const trainingConfig: Record<string, any> = {
      task_name: selectedEnv,
      num_envs: numEnvs,
      max_iterations: maxIterations,
      video_enabled: videoEnabled,
      video_interval: videoInterval,
      gpu_count: gpuCount,
      code_url: codeUrl.trim(),
    };

    if (codeRef.trim()) trainingConfig.code_ref = codeRef.trim();
    if (seed) trainingConfig.seed = parseInt(seed, 10);
    if (numSeeds > 1) trainingConfig.num_seeds = numSeeds;

    // Apply PPO single values
    for (const param of SWEEPABLE_PARAMS) {
      const val = ppoValues[param.key];
      if (val && !(param.key in sweepFields && sweepFields[param.key].length >= 2)) {
        if (param.type === 'float') {
          trainingConfig[param.key] = parseFloat(val);
        } else if (param.type === 'int') {
          trainingConfig[param.key] = parseInt(val, 10);
        } else {
          trainingConfig[param.key] = val;
        }
      }
    }

    const payload: any = {
      name: name.trim(),
      config: {
        mode: 'training' as const,
        training_config: trainingConfig,
      },
    };

    // Build sweep_configs from sweepFields
    const sweepConfigs = Object.entries(sweepFields)
      .filter(([, vals]) => vals.length >= 2)
      .map(([param, vals]) => ({
        parameter: param,
        values: vals.map((v) => {
          const n = Number(v);
          return isNaN(n) ? v : n;
        }),
      }));

    if (sweepConfigs.length > 0) {
      payload.config.sweep_configs = sweepConfigs;
    }

    payload.config.robot_id = robotId;

    try {
      const run = await createRun(payload);
      navigate(`/runs/${run.slug}`);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to create run');
      setSubmitting(false);
    }
  };

  // --- Derived helpers ---

  const selectedRobot = robots.find((r) => r.id === robotId);
  const selectedTier = GPU_TIERS.find((t) => t.count === gpuCount);

  // --- Step content ---

  const renderStep1 = () => (
    <Fade in timeout={300}>
      <Box>
        {/* GitHub repo picker */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            Repository
          </Typography>

          <Box sx={{ mb: 1.5 }}>
            <GitHubRepoPicker
              onSelect={(url, ref) => {
                setCodeUrl(url);
                setCodeRef(ref);
                setDiscoveredEnvs([]);
                setSelectedEnv('');
                setDiscoverError(null);
                // Auto-trigger discover
                setTimeout(() => handleDiscover(url), 0);
              }}
              value={codeUrl}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="contained"
                onClick={() => handleDiscover()}
                disabled={isDiscovering || !codeUrl.trim()}
                sx={{
                  backgroundColor: '#EF8997',
                  '&:hover': { backgroundColor: '#e5707f' },
                  minWidth: 110,
                }}
                startIcon={
                  isDiscovering ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
              >
                {isDiscovering ? 'Scanning...' : 'Discover'}
              </Button>
            </Box>
          </Box>

          {/* Discover error */}
          {discoverError && (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setDiscoverError(null)}>
              <LinkedError message={discoverError} />
            </Alert>
          )}

          {/* Discovered environments */}
          {discoveredEnvs.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, color: 'rgba(255,255,255,0.7)' }}>
                Environment
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {discoveredEnvs.map((env) => (
                  <Paper
                    key={env.id}
                    onClick={() => setSelectedEnv(env.id)}
                    sx={{
                      p: 2,
                      flex: '1 1 200px',
                      cursor: 'pointer',
                      border:
                        selectedEnv === env.id
                          ? '2px solid #EF8997'
                          : '1px solid rgba(255, 255, 255, 0.12)',
                      backgroundColor:
                        selectedEnv === env.id ? 'rgba(239, 137, 151, 0.08)' : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: selectedEnv === env.id ? '#EF8997' : 'rgba(255,255,255,0.3)',
                      },
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {env.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {env.file}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </>
          )}
        </Paper>

        {/* Robot */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            Robot
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={robotId}
            onChange={(e) => setRobotId(e.target.value)}
            helperText="Select the robot to train"
          >
            {robots.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Run Name */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            Run Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Auto-generated from robot + environment"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameManuallyEdited(true);
            }}
            size="small"
          />
        </Paper>
      </Box>
    </Fade>
  );

  const renderStep2 = () => (
    <Fade in timeout={300}>
      <Box>
        {/* Speed Tier */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            Speed Tier
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {GPU_TIERS.map((tier) => (
              <Paper
                key={tier.count}
                onClick={() => setGpuCount(tier.count)}
                sx={{
                  p: 2,
                  flex: 1,
                  cursor: 'pointer',
                  border:
                    gpuCount === tier.count
                      ? '2px solid #EF8997'
                      : '1px solid rgba(255, 255, 255, 0.12)',
                  backgroundColor:
                    gpuCount === tier.count ? 'rgba(239, 137, 151, 0.08)' : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: gpuCount === tier.count ? '#EF8997' : 'rgba(255,255,255,0.3)',
                  },
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {tier.count}x GPU
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF8997' }}>
                  {tier.label}
                </Typography>
                {'recommended' in tier && (tier as any).recommended && (
                  <Chip
                    label="Recommended"
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: 'rgba(239, 137, 151, 0.2)',
                      color: '#EF8997',
                      fontSize: 11,
                    }}
                  />
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {tier.desc}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {tier.time} &middot; {tier.cost}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* Training Iterations */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            Training Iterations
          </Typography>
          <TextField
            fullWidth
            type="number"
            size="small"
            value={maxIterations}
            onChange={(e) => setMaxIterations(parseInt(e.target.value, 10) || 0)}
          />
        </Paper>

        {/* Cost/time estimate */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor:
              estimate.total_cost > 500
                ? 'rgba(244, 67, 54, 0.08)'
                : estimate.total_cost > 100
                  ? 'rgba(255, 152, 0, 0.08)'
                  : 'rgba(239, 137, 151, 0.06)',
            border: `1px solid ${
              estimate.total_cost > 500
                ? 'rgba(244, 67, 54, 0.4)'
                : estimate.total_cost > 100
                  ? 'rgba(255, 152, 0, 0.4)'
                  : 'rgba(239, 137, 151, 0.3)'
            }`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                estimate.total_cost > 500
                  ? '#f44336'
                  : estimate.total_cost > 100
                    ? '#ff9800'
                    : 'rgba(255,255,255,0.7)',
              fontWeight: 500,
            }}
          >
            ~{estimate.task_time_hr.toFixed(1)} hr &middot; ~${estimate.total_cost.toFixed(2)}
            {totalTasks > 1 && ` (${totalTasks} tasks)`}
          </Typography>
        </Paper>

        {/* Sweep — preset-first design */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: sweepMode
              ? '1px solid rgba(239, 137, 151, 0.3)'
              : '1px solid rgba(255, 255, 255, 0.12)',
            transition: 'border-color 0.2s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: sweepMode ? 2 : 0 }}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Parameter Sweep
            </Typography>
            {sweepMode && (
              <Button
                size="small"
                onClick={clearSweep}
                sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: 12, minWidth: 0 }}
              >
                Clear
              </Button>
            )}
          </Box>

          {!sweepMode ? (
            /* Inactive state — pick a preset or go custom */
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
              {SWEEP_PRESETS.map((preset) => (
                <Paper
                  key={preset.label}
                  onClick={() => applyPreset(preset)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    flex: '1 1 140px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'rgba(239, 137, 151, 0.4)', backgroundColor: 'rgba(239, 137, 151, 0.04)' },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', mb: 0.25 }}>
                    {preset.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {preset.desc}{preset.seeds > 1 ? ` · ${preset.seeds} seeds` : ''}
                  </Typography>
                </Paper>
              ))}
              <Paper
                onClick={() => { setSweepMode(true); setSweepCustomOpen(true); }}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  flex: '1 1 140px',
                  border: '1px dashed rgba(255,255,255,0.12)',
                  backgroundColor: 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.3)' },
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TuneIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)', mb: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>
                  Custom
                </Typography>
              </Paper>
            </Box>
          ) : (
            /* Active sweep state */
            <Box>
              {/* Summary bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  mb: 2,
                  backgroundColor: 'rgba(239, 137, 151, 0.06)',
                  border: '1px solid rgba(239, 137, 151, 0.15)',
                }}
              >
                <Typography variant="body2" sx={{ color: '#EF8997', fontWeight: 500, flex: 1 }}>
                  {activeSweepCount > 0 ? `${activeSweepCount} parameter${activeSweepCount !== 1 ? 's' : ''}` : 'No parameters selected'}
                  {' · '}
                  {totalTasks} task{totalTasks !== 1 ? 's' : ''}
                  {' · '}
                  ~${estimate.total_cost.toFixed(2)}
                </Typography>
                <TextField
                  label="Seeds"
                  type="number"
                  size="small"
                  value={numSeeds}
                  onChange={(e) =>
                    setNumSeeds(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))
                  }
                  sx={{ width: 80 }}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Box>

              {/* Quick preset chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {SWEEP_PRESETS.map((preset) => (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    size="small"
                    onClick={() => applyPreset(preset)}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.6)',
                      '&:hover': { backgroundColor: 'rgba(239, 137, 151, 0.15)', color: '#EF8997' },
                      transition: 'all 0.15s',
                    }}
                  />
                ))}
              </Box>

              {/* Active sweep parameters — shown inline as editable rows */}
              {Object.entries(sweepFields).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {Object.entries(sweepFields).map(([key, vals]) => {
                    const paramDef = SWEEPABLE_PARAMS.find((p) => p.key === key);
                    return (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 1,
                          borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#EF8997', fontWeight: 500, minWidth: 120 }}>
                          {paramDef?.label || key}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                          {vals.map((v, i) => (
                            <Chip
                              key={i}
                              label={v}
                              size="small"
                              onDelete={() => handleSweepValuesChange(key, vals.filter((_, idx) => idx !== i))}
                              sx={{
                                backgroundColor: 'rgba(239, 137, 151, 0.12)',
                                color: '#EF8997',
                                '& .MuiChip-deleteIcon': { color: 'rgba(239, 137, 151, 0.4)', '&:hover': { color: '#EF8997' } },
                              }}
                            />
                          ))}
                          {vals.length < 2 && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', alignSelf: 'center' }}>
                              needs {2 - vals.length} more
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleSweepToggle(key)}
                          sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ff6b6b' } }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Expand to edit parameters */}
              <Button
                size="small"
                onClick={() => setSweepCustomOpen(!sweepCustomOpen)}
                startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  textTransform: 'none',
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.04)' },
                }}
              >
                {sweepCustomOpen ? 'Hide parameters' : 'Edit parameters'}
              </Button>

              <Collapse in={sweepCustomOpen}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {SWEEPABLE_PARAMS.map((param) => (
                      <Grid key={param.key} size={{ xs: 6 }}>
                        <SweepableField
                          label={param.key}
                          value={ppoValues[param.key] || ''}
                          sweepValues={sweepFields[param.key] || []}
                          isSweeping={param.key in sweepFields}
                          onValueChange={(v) => handlePpoValueChange(param.key, v)}
                          onSweepValuesChange={(v) => handleSweepValuesChange(param.key, v)}
                          onSweepToggle={() => handleSweepToggle(param.key)}
                          placeholder={param.placeholder}
                          type={param.type}
                          selectOptions={param.type === 'select' ? param.options : undefined}
                          sweepModeEnabled={sweepMode}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>

        {/* Advanced Settings */}
        <Accordion
          sx={{
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Advanced Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="num_envs"
                  type="number"
                  size="small"
                  value={numEnvs}
                  onChange={(e) => setNumEnvs(parseInt(e.target.value, 10) || 0)}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={videoEnabled}
                      onChange={(e) => setVideoEnabled(e.target.checked)}
                    />
                  }
                  label="video_enabled"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="video_interval"
                  type="number"
                  size="small"
                  value={videoInterval}
                  onChange={(e) => setVideoInterval(parseInt(e.target.value, 10) || 0)}
                  disabled={!videoEnabled}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="seed (optional)"
                  type="number"
                  size="small"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                />
              </Grid>
            </Grid>

            {/* PPO Settings — only shown when not in sweep mode */}
            {!sweepMode && (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, mt: 1, display: 'block' }}>
                  PPO Hyperparameters
                </Typography>
                <Grid container spacing={2}>
                  {SWEEPABLE_PARAMS.map((param) => (
                    <Grid key={param.key} size={{ xs: 6 }}>
                      {param.type === 'select' && param.options ? (
                        <TextField
                          select
                          fullWidth
                          label={param.key}
                          size="small"
                          value={ppoValues[param.key] || ''}
                          onChange={(e) => handlePpoValueChange(param.key, e.target.value)}
                        >
                          <MenuItem value="">Default</MenuItem>
                          {param.options.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          label={param.key}
                          size="small"
                          placeholder={param.placeholder}
                          value={ppoValues[param.key] || ''}
                          onChange={(e) => handlePpoValueChange(param.key, e.target.value)}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Fade>
  );

  const renderStep3 = () => (
    <Fade in timeout={300}>
      <Box>
        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2.5 }}>
            Review
          </Typography>

          {[
            ['Repository', codeUrl.replace('https://github.com/', '')],
            ['Environment', selectedEnv],
            ['Robot', selectedRobot?.name || robotId],
            ['Run Name', name || '(auto)'],
            ['Speed Tier', selectedTier ? `${selectedTier.count}x GPU — ${selectedTier.label}` : `${gpuCount}x GPU`],
            ['Iterations', maxIterations.toLocaleString()],
            ['Est. Cost', `$${estimate.total_cost.toFixed(2)}`],
            ['Est. Time', `~${estimate.task_time_hr.toFixed(1)} hr`],
            ...(totalTasks > 1 ? [['Sweep Tasks', `${totalTasks} tasks`]] : []),
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>
    </Fade>
  );

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/runs')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h3" sx={{ fontWeight: 600 }}>
          New Training Run
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper
        activeStep={step}
        sx={{
          mb: 4,
          '& .MuiStepIcon-root': {
            color: 'rgba(255,255,255,0.2)',
            '&.Mui-active': { color: '#EF8997' },
            '&.Mui-completed': { color: '#EF8997' },
          },
          '& .MuiStepLabel-label': {
            color: 'rgba(255,255,255,0.5)',
            '&.Mui-active': { color: '#fff' },
            '&.Mui-completed': { color: 'rgba(255,255,255,0.7)' },
          },
          '& .MuiStepConnector-line': {
            borderColor: 'rgba(255,255,255,0.12)',
          },
        }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <LinkedError message={error} />
        </Alert>
      )}

      {/* Step content */}
      {step === 0 && renderStep1()}
      {step === 1 && renderStep2()}
      {step === 2 && renderStep3()}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            if (step === 0) navigate('/runs');
            else setStep(step - 1);
          }}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        {step < 2 ? (
          <Button
            variant="contained"
            onClick={() => setStep(step + 1)}
            disabled={step === 0 ? !step1Complete : !step2Complete}
            sx={{
              backgroundColor: '#EF8997',
              '&:hover': { backgroundColor: '#e5707f' },
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !name.trim() || !robotId || !selectedEnv || totalTasks > MAX_TASKS}
            sx={{
              backgroundColor: '#EF8997',
              '&:hover': { backgroundColor: '#e5707f' },
            }}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting
              ? 'Starting...'
              : totalTasks > 1
                ? `Start Sweep (${totalTasks} tasks)`
                : 'Start Training'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NewRun;
